import * as React from 'react';
import { Button, Modal, Spinner, Table } from 'react-bootstrap';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { VscDash } from 'react-icons/vsc';
import { CgClose } from 'react-icons/cg';
import { shallowEqual } from 'react-redux';

import { FrequencyCurveChart } from '../../../components/Charts/Osc/FrequencyCurveChart';

import { useAppSelector } from '../../../redux/store';
import { OscillometryMeasurement } from '../../../Types/Common/src/measurement';

import './OscillometryAcceptabilityCriteriaModal.scss';

const { useState, useEffect, useRef, useCallback } = React;

/**
 * The 7 acceptability criteria keys for oscillometry waveforms.
 * These correspond to the fields on OscillometryWaveformPUTBody and
 * the acceptabilityCriteria object on the waveform measurement.
 */
const CRITERIA_DEFINITIONS = [
  {
    key: 'healthyResistanceCurve',
    label: 'Oscillogram has a resistance curve representative of healthy, asthmatic or COPD patients',
    editable: true,
  },
  {
    key: 'healthyReactanceCurve',
    label: 'Oscillogram has a reactance curve representative of healthy, asthmatic or COPD patients',
    editable: true,
  },
  {
    key: 'noAbnormalities',
    label: 'No visible abnormalities at all frequencies on oscillogram indicating non-pathological obstructions or leaks',
    editable: true,
  },
  {
    key: 'noPanting',
    label: 'No signs of panting/excessively rapid breathing (<30 breaths per minute)',
    editable: true,
  },
  {
    key: 'noDecreasedBreathing',
    label: 'No signs of decreased breathing suggestive of hypoventilation frequency (>8 breaths per minute)',
    editable: true,
  },
  {
    key: 'noTongueOrHeadtilt',
    label: 'No visible signs of tongue in mouthpiece or head tilted downward during measurement',
    editable: false,
  },
  {
    key: 'noLeaksOrMissingNoseClip',
    label: 'No visible signs of leak around the mouthpiece or a missing nose clip',
    editable: false,
  },
] as const;

type CriteriaKey = typeof CRITERIA_DEFINITIONS[number]['key'];

// Chart responsive constraints
const CHART_ASPECT_RATIO = 5 / 4;
const CHART_MAX_WIDTH = 550;
const CHART_MIN_WIDTH = 280;
const CHART_CONTAINER_PADDING = 32;

interface OscillometryAcceptabilityCriteriaModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  waveform: OscillometryMeasurement;
  waveformNum: number;
  isReadOnly: boolean | undefined;
  onSave: (waveform: OscillometryMeasurement, criteria: Partial<Record<CriteriaKey, boolean>>) => Promise<void>;
}

export const OscillometryAcceptabilityCriteriaModal: React.FC<OscillometryAcceptabilityCriteriaModalProps> = ({
  show,
  setShow,
  waveform,
  waveformNum,
  isReadOnly,
  onSave,
}) => {
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const [isSaving, setIsSaving] = useState(false);

  // Responsive chart sizing — measures the container and derives width/height
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(CHART_MAX_WIDTH);

  const updateChartSize = useCallback(() => {
    if (!chartContainerRef.current) return;
    const containerWidth = chartContainerRef.current.clientWidth;
    const clamped = Math.max(CHART_MIN_WIDTH, Math.min(CHART_MAX_WIDTH, containerWidth - CHART_CONTAINER_PADDING));
    setChartWidth(clamped);
  }, []);

  useEffect(() => {
    if (!show) return;

    // Small delay so the modal has rendered and we can measure
    const timeoutId = setTimeout(updateChartSize, 50);
    window.addEventListener('resize', updateChartSize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateChartSize);
    };
  }, [show, updateChartSize]);

  const chartHeight = Math.round(chartWidth / CHART_ASPECT_RATIO);

  // Local draft state for criteria — initialized from the waveform's existing acceptabilityCriteria
  const [draftCriteria, setDraftCriteria] = useState<Partial<Record<CriteriaKey, boolean>>>({});

  // Re-initialize draft whenever modal opens or the waveform changes
  useEffect(() => {
    if (show) {
      const initial: Partial<Record<CriteriaKey, boolean>> = {};
      for (const { key, editable } of CRITERIA_DEFINITIONS) {
        if (editable) {
          // Default to true (pass) if not already set
          initial[key] = waveform.acceptabilityCriteria?.[key] ?? true;
        }
      }
      setDraftCriteria(initial);
    }
  }, [show, waveform]);

  const handleClose = () => {
    if (isSaving) return;
    setShow(false);
  };

  const toggleCriterion = (key: CriteriaKey) => {
    if (isReadOnly || isSaving) return;
    setDraftCriteria(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    if (isReadOnly || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(waveform, draftCriteria);
      setShow(false);
    } catch (e) {
      console.error('Error saving acceptability criteria:', e);
    } finally {
      setIsSaving(false);
    }
  };

  // Check if draft differs from what's currently saved on the waveform
  const hasChanges = (): boolean => {
    const saved = waveform.acceptabilityCriteria ?? {};
    for (const { key, editable } of CRITERIA_DEFINITIONS) {
      if (!editable) continue;
      const savedVal = saved[key] ?? true; // default to true
      const draftVal = draftCriteria[key];
      if (savedVal !== draftVal) return true;
    }
    return false;
  };

  const renderCriterionValue = (key: CriteriaKey) => {
    const value = draftCriteria[key];
    const savedValue = waveform.acceptabilityCriteria?.[key] ?? true; // default to true if not yet saved
    const isModified = value !== savedValue;

    return (
      <span style={{ position: 'relative' }}>
        {value
          ? <IoIosCheckmarkCircle size={22} className="text-success" />
          : <VscDash size={22} className="text-danger" />
        }
        {isModified && <span className="criteria-modified-indicator" />}
      </span>
    );
  };

  return (
    <Modal onHide={handleClose} show={show} size="xl" className="OscillometryAcceptabilityCriteriaModal">
      <Modal.Header className="d-flex flex-column pb-0">
        <Modal.Title>
          {translationText.acceptabilityCriteria || 'Acceptability Criteria'} {isReadOnly && '(Read-only)'} — {translationText.measurementNumber || 'Measurement'} {waveformNum}
        </Modal.Title>
        <p className="mb-1 mt-1">
          {translationText.oscillometryAcceptabilityDescription || 'Review acceptability requirements for this oscillometry waveform. Double-click a cell to toggle its value.'}
        </p>
        <CgClose size="30px" className="Close" onClick={handleClose} cursor="pointer" data-testid="OscAcceptabilityModal-Close" />
      </Modal.Header>

      <Modal.Body>
        {/* Frequency Curve Chart — responsive container */}
        <div ref={chartContainerRef} className="chart-responsive-container">
          <div className="chart-responsive-inner">
            <h5 style={{ textAlign: 'center', fontWeight: 'bold' }}>
              {translationText.frequencyCurve || 'Frequency Curve'}
            </h5>
            <FrequencyCurveChart
              frequencyCurve={waveform?.frequencyCurve}
              width={chartWidth}
              height={chartHeight}
              showLegend
            />
          </div>
        </div>

        {/* Criteria Table */}
        <Table bordered className="criteria-table">
          <thead>
            <tr>
              <th>{translationText.acceptabilityRequirements || 'Acceptability Requirements'}</th>
              <th className="text-center" style={{ width: 140 }}>
                {translationText.acceptReject || 'Accept / Reject'}
              </th>
            </tr>
          </thead>
          <tbody>
            {CRITERIA_DEFINITIONS.map(({ key, label, editable }, index) => {
              // Insert "Observational Criterion" header 
              const isFirstObservational = !editable && (index === 0 || CRITERIA_DEFINITIONS[index - 1].editable);

              return (
                <React.Fragment key={key}>
                  {isFirstObservational && (
                    <tr>
                      <td colSpan={2} className="font-weight-bold bg-light">
                        {translationText.observational || 'Observational'} {translationText.criterion || 'Criterion'}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      {index + 1}. {translationText[key] || label}
                      {!editable && <sup>§</sup>}
                    </td>
                    {editable ? (
                      <td
                        className={`text-center ${!isReadOnly && !isSaving ? 'hoverCell' : ''}`}
                        onDoubleClick={() => toggleCriterion(key)}
                        style={{
                          userSelect: 'none',
                          cursor: !isReadOnly && !isSaving ? 'pointer' : 'default',
                        }}
                      >
                        {renderCriterionValue(key)}
                      </td>
                    ) : (
                      <td className="text-center font-weight-bold">
                        N/A<sup>§</sup>
                      </td>
                    )}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>

        <div className="mt-3">
          {!isReadOnly && <small className="text-muted d-block">
            {translationText.doubleClickToToggle || 'Double-click a cell to toggle between Accept and Reject'}
          </small>}
          <small className="text-muted d-block mt-1">
            <sup>§</sup> {translationText.observationalCriterionFootnote || 'Observational criterion — cannot be determined from the oscillogram alone. These criteria require direct observation of the patient during the measurement and are not assessable during remote overreading.'}
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          {translationText.cancel || 'Cancel'}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isReadOnly || isSaving || !hasChanges()}
        >
          {isSaving
            ? <><Spinner animation="border" size="sm" className="mr-2" />{translationText.saving || 'Saving...'}</>
            : translationText.save || 'Save'
          }
        </Button>
      </Modal.Footer>
    </Modal>
  );
};